<script setup>
import moment from 'moment';
</script>

<template>
  <DashboardLayout :title="$t('common.settings')">
    <main class="w-full flex flex-col gap-8 pb-16">
      <header class="flex flex-col gap-5 sm:flex-row sm:items-center">
        <img class="h-16 w-16 rounded-full ring-2 ring-gray-800" :src="imageURL" alt="" />
        <div class="flex flex-1 flex-col gap-1 min-w-0">
          <h1 class="text-text-primary text-[32px] font-semibold leading-tight">{{ $t('common.settings') }}</h1>
          <p class="flex flex-wrap items-center gap-x-2 text-sm text-text-secondary">
            <span class="text-text-primary font-medium">{{ user?.steam_profile?.personaname || user.steam_id }}</span>
            <span aria-hidden="true">·</span>
            <span
              :class="{ 'blur-sm': user.streamer_mode }"
              :title="user.streamer_mode ? $t('profile.settings.email_hidden') : null"
            >{{ user?.email }}</span>
          </p>
        </div>
        <router-link
          :to="`user/${user.uuid}`"
          class="self-start sm:self-auto text-text-primary bg-gray-800 text-xs font-medium rounded-md px-3 py-2.5 hover:bg-gray-700 duration-300"
        >
          {{ $t('profile.settings.view_public_profile') }}
        </router-link>
      </header>

      <nav
        class="-mx-4 flex gap-6 overflow-x-auto px-4 shadow-[inset_0_-1px_0_theme(colors.gray.800)] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:mx-0 sm:px-0"
        role="tablist"
        :aria-label="$t('common.settings')"
      >
        <button
          v-for="tab in tabs"
          :key="tab.key"
          :id="`settings-tab-${tab.key}`"
          type="button"
          role="tab"
          :aria-selected="activeTab === tab.key"
          :aria-controls="`settings-panel-${tab.key}`"
          class="whitespace-nowrap border-b-2 pb-3 text-sm font-medium duration-200"
          :class="activeTab === tab.key
            ? 'border-primary-500 text-text-primary'
            : 'border-transparent text-text-secondary hover:text-text-primary hover:border-gray-600'"
          @click="selectTab(tab.key)"
        >
          {{ $t(`profile.settings.tabs.${tab.key}`) }}
        </button>
      </nav>

      <section
        v-show="activeTab === 'account'"
        id="settings-panel-account"
        role="tabpanel"
        aria-labelledby="settings-tab-account"
        class="flex flex-col gap-6"
      >
        <SettingsCard
          :title="$t('profile.settings.profile_section')"
          :description="$t('profile.settings.profile_section_text')"
        >
          <div class="px-6">
            <dl class="divide-y divide-gray-800">
              <div class="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4">
                <dt class="text-text-primary">{{ $t('common.name') }}</dt>
                <dd class="mt-1 flex text-text-secondary sm:mt-0 sm:col-span-2">
                  <span class="flex-grow">{{ username }}</span>
                </dd>
              </div>
              <div class="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:pt-5">
                <dt class="text-text-primary my-auto">{{ $t('profile.settings.avatar') }}</dt>
                <dd
                  class="mt-1 flex text-sm text-text-primary sm:mt-0 sm:col-span-2"
                >
                  <span class="flex-grow">
                    <img class="h-8 w-8 rounded-full" :src="imageURL" alt="" />
                  </span>
                </dd>
              </div>
              <div
                class="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:pt-5 items-center"
              >
                <dt class="text-text-primary my-auto">{{ $t('profile.settings.email') }}</dt>
                <dd
                  class="mt-1 flex text-text-secondary sm:mt-0 sm:col-span-2 items-center"
                >
                  <span class="flex-grow" :class="{ 'blur-sm': user.streamer_mode }" :title="user.streamer_mode ? $t('profile.settings.email_hidden') : null">{{ user.email }}</span>
                  <span class="ml-4 flex-shrink-0">
                    <button
                      @click="toggleEmailChangeModal"
                      class="bg-gray-800 rounded-lg text-xs text-text-primary px-3 py-2.5 hover:bg-gray-700 duration-300"
                    >
                      {{ $t('profile.settings.change_email') }}
                    </button>
                  </span>
                </dd>
              </div>
            </dl>
          </div>
        </SettingsCard>
        <SettingsCard
          danger
          :title="$t('profile.settings.danger_zone')"
          :description="$t('profile.settings.danger_zone_text')"
        >
          <div class="px-6">
            <dl class="divide-y divide-gray-800">
              <div
                class="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:pt-5 items-center"
              >
                <dt
                  class="text-text-primary my-auto flex items-center flex items-center"
                >
                  {{ $t('profile.settings.delete_account') }}
                </dt>
                <dd
                  class="mt-1 flex text-sm text-text-primary sm:mt-0 sm:col-span-2"
                >
                  <a
                    class="cursor-pointer px-8 py-1 border flex items-center gap-2 select-none border-transparent rounded-md shadow-sm text-sm font-medium text-text-primary bg-danger-600 hover:bg-danger-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800"
                    @click="toggleDeleteAccountConfirmation"
                  >
                    {{ $t('profile.settings.delete_account') }}
                  </a>
                </dd>
              </div>
            </dl>
          </div>
        </SettingsCard>
      </section>

      <section
        v-show="activeTab === 'connections'"
        id="settings-panel-connections"
        role="tabpanel"
        aria-labelledby="settings-tab-connections"
        class="flex flex-col gap-6"
      >
        <SettingsCard
          :title="$t('profile.settings.connections_section')"
          :description="$t('profile.settings.connections_section_text')"
        >
          <div class="px-6">
            <dl class="divide-y divide-gray-800">
              <div
                class="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:pt-5 items-center"
              >
                <dt class="text-text-primary">{{ $t('profile.settings.steam_id') }}</dt>
                <dd
                  class="mt-1 flex text-text-secondary sm:mt-0 sm:col-span-2 items-center"
                >
                  <span class="flex-grow" :class="{ 'blur-sm': user.streamer_mode }" :title="user.streamer_mode ? $t('profile.settings.steam_id_hidden') : null">{{ user.steam_id }}</span>
                  <span
                    class="ml-4 flex-shrink-0 flex items-start space-x-4"
                  >
                    <button
                      @click="showSteamClearConfirmation()"
                      :disabled="!showChangeSteamAccount()"
                      class="has-tooltip relative bg-gray-800 rounded-lg text-xs text-text-primary px-3 py-2.5 disabled:text-text-disabled disabled:cursor-not-allowed"
                    >
                      {{ $t('profile.settings.change_steam_account') }}
                      <div
                        v-if="!showChangeSteamAccount()"
                        class="tooltip bottom-full right-0 mb-2 w-64 bg-gray-900 rounded-lg p-2 text-left text-text-primary font-light"
                      >
                        {{ $t('profile.settings.steam_change_limit') }}
                      </div>
                    </button>
                  </span>
                </dd>
              </div>
              <div class="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:pt-5">
                <dt class="text-text-primary my-auto">Discord</dt>
                <dd
                  class="mt-1 flex text-sm text-text-primary sm:mt-0 sm:col-span-2 items-center"
                >
                  <span class="flex-grow"
                  ><button
                     class="px-8 py-1 border flex items-center gap-2 select-none border-transparent rounded-md shadow-sm text-sm font-medium text-text-primary bg-primary-600 hover:bg-primary-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-indigo-500"
                     @click="link_discord"
                     v-if="!discord_tag"
                   >
                     <img
                       src="../assets/icons/discord-white.svg"
                       :alt="$t('profile.settings.discord_icon_alt')"
                       class="w-4 h-4"
                     />
                     {{ $t('profile.settings.link_discord') }}
                   </button>
                    <span
                      v-else
                      class="flex flex-row"
                      :class="{ 'blur-sm': user.streamer_mode }"
                      :title="user.streamer_mode ? $t('profile.settings.discord_hidden') : null"
                    >{{ discord_tag }}
                      <XMarkIcon
                        class="w-4 h-4 text-gray-500 cursor-pointer"
                        @click="unlinkTarget = 'discord'" /></span
                    ></span>
                  <span v-if="discord_tag" class="ml-4 flex-shrink-0">
                    <button
                      @click="syncDiscordRoles"
                      class="bg-gray-800 rounded-lg text-xs text-text-primary px-3 py-2.5 hover:bg-gray-700 duration-300"
                    >
                      <span v-if="syncingDiscord">{{ $t('profile.settings.syncing') }}</span>
                      <span v-else>{{ $t('profile.settings.sync_roles') }}</span>
                    </button>
                  </span>
                </dd>
              </div>
              <div v-if="user.twitch_available" class="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:pt-5">
                <dt class="text-text-primary my-auto">Twitch</dt>
                <dd class="mt-1 flex text-sm text-text-primary sm:mt-0 sm:col-span-2 items-center">
                  <button
                    v-if="!user.twitch"
                    class="px-8 py-1 border flex items-center gap-2 select-none border-transparent rounded-md shadow-sm text-sm font-medium text-text-primary bg-primary-600 hover:bg-primary-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-indigo-500"
                    @click="link_twitch"
                  >
                    <TwitchIcon class="w-4 h-4" />
                    {{ $t('profile.settings.link_twitch') }}
                  </button>
                  <span
                    v-else
                    class="flex flex-row items-center gap-1"
                    :class="{ 'blur-sm': user.streamer_mode }"
                  >{{ user.twitch.login }}
                    <XMarkIcon
                      class="w-4 h-4 text-gray-500 cursor-pointer"
                      :aria-label="$t('profile.settings.unlink_twitch')"
                      @click="unlinkTarget = 'twitch'" /></span>
                </dd>
              </div>
              <div class="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:pt-5">
                <dt class="text-text-primary my-auto">{{ $t('profile.settings.steam_bot') }}</dt>
                <dd
                  class="mt-1 flex text-sm text-text-primary sm:mt-0 sm:col-span-2"
                >
                  <span class=""
                  ><a
                     class="px-8 py-1 border flex items-center gap-2 select-none border-transparent rounded-md shadow-sm text-sm font-medium text-text-primary bg-primary-600 hover:bg-primary-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-indigo-500"
                     :href="steamBotLink"
                     target="_blank"
                     v-if="!user.has_steam_bot_added"
                   >
                     <img
                       src="../assets/icons/steam.svg"
                       :alt="$t('profile.settings.steam_icon_alt')"
                       class="w-4 h-4"
                     />
                     {{ $t('profile.settings.add_to_friends') }}
                   </a>
                    <span v-else class="flex flex-row gap-2 text-success-500">
                      <CheckIcon class="w-4 h-4" />
                      {{ $t('profile.settings.added_to_friends') }}
                    </span></span
                  >
                </dd>
              </div>
              <div class="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:pt-5">
                <dt class="text-text-primary my-auto">Faceit</dt>
                <dd
                  class="mt-1 flex text-sm text-text-primary sm:mt-0 sm:col-span-2 items-center"
                >
                  <span class="flex-grow"
                  ><button
                     class="px-8 py-1 border flex items-center gap-2 select-none border-transparent rounded-md shadow-sm text-sm font-medium text-text-primary bg-primary-600 hover:bg-primary-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-indigo-500"
                     @click="link_faceit"
                     v-if="!user.faceit_nickname"
                   >
                     <img
                       src="../assets/icons/faceit-icon.svg"
                       :alt="$t('profile.settings.faceit_icon_alt')"
                       class="w-4 h-4"
                     />
                     {{ $t('profile.settings.link_faceit') }}
                   </button>
                    <span
                      v-else
                      class="flex flex-row"
                      :class="{ 'blur-sm': user.streamer_mode }"
                      :title="user.streamer_mode ? $t('profile.settings.faceit_hidden') : null"
                    >{{ user.faceit_nickname }}
                      <XMarkIcon
                        class="w-4 h-4 text-gray-500 cursor-pointer"
                        @click="unlinkTarget = 'faceit'"
                      />
                    </span>
                  </span>
                </dd>
              </div>
              <div class="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:pt-5">
                <dt class="text-text-primary my-auto">{{ $t('profile.settings.wooting_detection') }}</dt>
                <dd
                  class="mt-1 flex text-sm text-text-primary sm:mt-0 sm:col-span-2"
                >
                  <span
                    v-if="!hidDetectionAvailable && !user.users_wooting"
                    class="flex flex-row gap-2 text-danger-500"
                  >
                    <XMarkIcon class="w-4 h-4" />
                    {{ $t('profile.settings.wooting_unavailable') }}
                  </span>
                  <a
                    class="px-8 py-1 border flex items-center gap-2 select-none border-transparent rounded-md shadow-sm text-sm font-medium text-text-primary bg-primary-600 hover:bg-primary-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-indigo-500"
                    @click="getKeyboard"
                    target="_blank"
                    v-else-if="!user.uses_wooting"
                  >
                    <img
                      src="../assets/icons/wooting_logo_white.svg"
                      :alt="$t('profile.settings.steam_icon_alt')"
                      class="w-4 h-4"
                    />
                    {{ $t('profile.settings.detect_wooting') }}
                  </a>
                  <span v-else class="flex flex-row gap-2 text-success-500">
                    <CheckIcon class="w-4 h-4" />
                    {{ $t('profile.settings.wooting_detected') }}
                  </span>
                </dd>
              </div>
            </dl>
          </div>
        </SettingsCard>
        <SettingsCard
          :title="$t('profile.settings.match_tracking_section')"
          :description="$t('profile.settings.match_tracking_section_text')"
        >
          <div class="px-6">
            <dl class="divide-y divide-gray-800">
              <div
                class="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:pt-5 items-center"
              >
                <dt class="text-text-primary my-auto flex items-center">
                  {{ $t('profile.settings.steam_match_code') }}
                  <a
                    href="https://help.steampowered.com/en/wizard/HelpWithGameIssue/?appid=730&issueid=128&ref=refrag.gg"
                    target="_blank"
                    class="ml-2 text-text-secondary underline text-xs"
                  >{{ $t('profile.settings.get_code_here') }}</a
                  >
                </dt>
                <dd
                  v-if="steamMatchCode !== 'CSGO - ***** - ***** - ***** - *****'"
                  class="mt-1 flex text-sm text-text-primary sm:mt-0 sm:col-span-2"
                >
                  <input
                    @input="steamMatchCode = $event.target.value"
                    placeholder="CSGO - XXXXX - XXXXX - XXXXX - XXXXX"
                    class="block w-1/3 rounded-md bg-gray-800 border-gray-700 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm text-text-secondary"
                    type="text"
                    :value="steamMatchCode"
                  />
                  <Transition>
                    <button
                      type="button"
                      @click="saveSteamMatchCode"
                      class="mt-3 inline-flex w-full items-center justify-center rounded-md border border-transparent bg-primary-600 px-4 py-2 font-medium text-text-primary shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                    >
                      {{ $t('common.save') }}
                    </button>
                  </Transition>
                </dd>
                <dd
                  v-else
                  class="mt-1 flex text-sm text-text-primary sm:mt-0 sm:col-span-2 items-center"
                >
                  <h1 class="text-text-secondary">{{ steamMatchCode }}</h1>
                  <button
                    type="button"
                    @click="clearMatchCode"
                    class="mt-3 inline-flex w-full items-center justify-center rounded-md border border-transparent bg-primary-600 px-2 py-2 font-medium text-text-primary shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    <img
                      src="../assets/icons/trash.svg"
                      :alt="$t('profile.settings.clear_icon_alt')"
                      class="w-4 h-4 brightness-200"
                    />
                  </button>
                </dd>
              </div>
              <div
                class="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:pt-5 items-center"
              >
                <dt
                  class="text-text-primary my-auto flex items-center flex items-center"
                >
                  {{ $t('profile.settings.steam_auth_code') }}
                  <a
                    href="https://help.steampowered.com/en/wizard/HelpWithGameIssue/?appid=730&issueid=128&ref=refrag.gg"
                    target="_blank"
                    class="ml-2 text-text-secondary underline text-xs"
                  >{{ $t('profile.settings.get_code_here') }}</a
                  >
                </dt>
                <dd
                  v-if="authenticationCode !== user.match_auth_token"
                  class="mt-1 flex text-sm text-text-primary sm:mt-0 sm:col-span-2"
                >
                  <input
                    @input="authenticationCode = $event.target.value"
                    placeholder="XXXX - XXXXX - XXXX"
                    class="block w-1/3 rounded-md bg-gray-800 border-gray-700 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm text-text-secondary"
                    type="text"
                    :value="authenticationCode"
                  />
                  <Transition>
                    <button
                      type="button"
                      @click="saveSteamAuthCode"
                      class="mt-3 inline-flex w-full items-center justify-center rounded-md border border-transparent bg-primary-600 px-4 py-2 font-medium text-text-primary shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                    >
                      {{ $t('common.save') }}
                    </button>
                  </Transition>
                </dd>
                <dd
                  v-else
                  class="mt-1 flex text-sm text-text-primary sm:mt-0 sm:col-span-2 items-center"
                >
                  <h1 class="text-text-secondary">{{ authenticationCode }}</h1>
                  <button
                    type="button"
                    @click="clearSteamAuthCode"
                    class="mt-3 inline-flex w-full items-center justify-center rounded-md border border-transparent bg-primary-600 px-2 py-2 font-medium text-text-primary shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    <img
                      src="../assets/icons/trash.svg"
                      :alt="$t('profile.settings.clear_icon_alt')"
                      class="w-4 h-4 brightness-200"
                    />
                  </button>
                </dd>
              </div>
            </dl>
          </div>
        </SettingsCard>
      </section>

      <section
        v-show="activeTab === 'play'"
        id="settings-panel-play"
        role="tabpanel"
        aria-labelledby="settings-tab-play"
        class="flex flex-col gap-6"
      >
        <SettingsCard
          :title="$t('profile.settings.play_section')"
          :description="$t('profile.settings.play_section_text')"
        >
          <div class="px-6">
            <dl class="divide-y divide-gray-800">
              <div
                class="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 items-center"
              >
                <dt class="font-medium text-text-primary">
                  {{ $t('profile.settings.server_location') }}
                  <p class="max-w-2xl text-xs text-text-secondary font-normal">
                    {{ $t('profile.settings.server_location_text') }}
                  </p>
                </dt>
                <dd class="mt-1 flex text-text-secondary sm:mt-0 sm:col-span-2">
                  <div class="w-full sm:w-[300px]">
                    <SettingsDropdown
                      :model-value="activeLocation?.id"
                      :options="locationOptions"
                      :label="$t('profile.settings.server_location')"
                      @update:model-value="selectLocationById"
                    >
                      <template #selected>
                        <flag :rounded="true" :iso="activeLocation?.flag" class="rounded-full h-4 w-4" />
                        {{ activeLocation?.name }}
                        <span class="text-text-disabled">•</span>
                        {{ latencyLabelFor(activeLocation?.value) }}
                      </template>
                      <template #option="{ option }">
                        <flag :rounded="true" :iso="option.location.flag" class="rounded-full h-4 w-4" />
                        <span class="flex-1">{{ option.label }}</span>
                        <span class="text-text-secondary">{{ latencyLabel(option.location) }}</span>
                      </template>
                    </SettingsDropdown>
                  </div>
                </dd>
              </div>
              <div
                class="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 items-center"
              >
                <dt class="font-medium text-text-primary">
                  {{ $t('profile.settings.server_language') }}
                  <p class="max-w-2xl text-xs text-text-secondary font-normal">
                    {{ $t('profile.settings.server_language_text') }}
                  </p>
                </dt>
                <dd class="mt-1 flex text-text-secondary sm:mt-0 sm:col-span-2">
                  <div class="w-full sm:w-[300px]">
                    <SettingsDropdown
                      :model-value="selectedLanguage?.code"
                      :options="languageOptions"
                      :label="$t('profile.settings.server_language')"
                      @update:model-value="selectLanguageByCode"
                    />
                  </div>
                </dd>
              </div>
              <div
                id="beta-access"
                class="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:pt-5 items-center"
              >
                <dt class="text-sm font-medium text-text-primary">
                  {{ $t('profile.settings.beta_access') }}
                  <p class="max-w-2xl text-xs text-text-secondary font-normal">
                    {{ $t('profile.settings.beta_access_text') }}
                  </p>
                </dt>

                <dd
                  class="mt-1 flex text-sm text-text-primary sm:mt-0 sm:col-span-2 items-center"
                >
                  <input
                    type="checkbox"
                    :value="betaAccessVal"
                    v-model="betaAccessVal"
                    v-on:change="saveBetaAccess()"
                    class="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                  />
                </dd>
              </div>
              <div
                class="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:pt-5 items-center"
              >
                <dt class="text-sm font-medium text-text-primary">
                  {{ $t('profile.settings.streamer_mode') }}
                  <p class="max-w-2xl text-xs text-text-secondary font-normal">
                    {{ $t('profile.settings.streamer_mode_text') }}
                  </p>
                </dt>

                <dd
                  class="mt-1 flex text-sm text-text-primary sm:mt-0 sm:col-span-2 items-center"
                >
                  <input
                    type="checkbox"
                    :value="streamerModVal"
                    v-model="streamerModVal"
                    v-on:change="saveStreamerMode()"
                    class="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                  />
                </dd>
              </div>
              <!-- Allstar highlights disabled
              <div
                class="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:pt-5 items-center"
              >
                <dt class="text-sm font-medium text-text-primary">
                  Allstar Enabled
                  <p class="max-w-2xl text-xs text-text-secondary font-normal">
                    Allow us to automatically generate Allstar clips for your
                    matches and high scores?
                  </p>
                </dt>

                <dd
                  class="mt-1 flex text-sm text-text-primary sm:mt-0 sm:col-span-2 items-center"
                >
                  <input
                    type="checkbox"
                    :value="allstarEnabledVal"
                    v-model="allstarEnabledVal"
                    v-on:change="saveAllstarEnabled()"
                    class="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                  />
                </dd>
              </div>
              -->
            </dl>
          </div>
        </SettingsCard>
        <CoachSettings
          :user="user"
          @settings-updated="handleCoachSettingsUpdate"
        />
      </section>

      <section
        v-if="activeTab === 'audio'"
        id="settings-panel-audio"
        role="tabpanel"
        aria-labelledby="settings-tab-audio"
      >
        <AudioSettings />
      </section>

      <section
        v-show="activeTab === 'notifications'"
        id="settings-panel-notifications"
        role="tabpanel"
        aria-labelledby="settings-tab-notifications"
        class="flex flex-col gap-6"
      >
        <SettingsCard
          :title="$t('profile.settings.notifications_section')"
          :description="$t('profile.settings.notifications_section_text')"
        >
          <div class="flex flex-col w-full divide-y divide-gray-800 px-6">
            <div class="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4">
              <dt class="text-sm font-medium text-text-primary">
                {{ $t('profile.settings.bot_match_notifications') }}
                <p class="max-w-2xl text-xs text-text-secondary font-normal">
                  {{ $t('profile.settings.bot_match_notifications_text') }}
                </p>
              </dt>

              <dd
                class="mt-1 flex text-sm text-text-primary sm:mt-0 sm:col-span-2 items-center"
              >
                <input
                  type="checkbox"
                  :value="steam_bot_match_notifications"
                  v-model="steam_bot_match_notifications"
                  v-on:change="saveSteamBotSettings()"
                  class="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                />
              </dd>
            </div>

            <div class="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4">
              <dt class="text-sm font-medium text-text-primary">
                {{ $t('profile.settings.bot_routine_notifications') }}
                <p class="max-w-2xl text-xs text-text-secondary font-normal">
                  {{ $t('profile.settings.bot_routine_notifications_text') }}
                </p>
              </dt>

              <dd
                class="mt-1 flex text-sm text-text-primary sm:mt-0 sm:col-span-2 items-center"
              >
                <input
                  type="checkbox"
                  :value="steam_bot_routine_notifications"
                  v-model="steam_bot_routine_notifications"
                  v-on:change="saveSteamBotSettings()"
                  class="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                />
              </dd>
            </div>

            <div class="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4">
              <dt class="text-sm font-medium text-text-primary">
                {{ $t('profile.settings.bot_server_notifications') }}
                <p class="max-w-2xl text-xs text-text-secondary font-normal">
                  {{ $t('profile.settings.bot_server_notifications_text') }}
                </p>
              </dt>

              <dd
                class="mt-1 flex text-sm text-text-primary sm:mt-0 sm:col-span-2 items-center"
              >
                <input
                  type="checkbox"
                  :value="steam_bot_server_notifications"
                  v-model="steam_bot_server_notifications"
                  v-on:change="saveSteamBotSettings()"
                  class="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                />
              </dd>
            </div>

            <div class="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4">
              <dt class="text-sm font-medium text-text-primary">
                {{ $t('profile.settings.access_code_emails') }}
                <p class="max-w-2xl text-xs text-text-secondary font-normal">
                  {{ $t('profile.settings.access_code_emails_text') }}
                </p>
              </dt>

              <dd
                class="mt-1 flex text-sm text-text-primary sm:mt-0 sm:col-span-2 items-center"
              >
                <input
                  type="checkbox"
                  :value="accessCodeEmailsVal"
                  v-model="accessCodeEmailsVal"
                  v-on:change="saveEmailSettings()"
                  class="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                />
              </dd>
            </div>

            <div class="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4">
              <dt class="text-sm font-medium text-text-primary">
                {{ $t('profile.settings.notification_emails') }}
                <p class="max-w-2xl text-xs text-text-secondary font-normal">
                  {{ $t('profile.settings.notification_emails_text') }}
                </p>
              </dt>
              <dd
                class="mt-1 flex text-sm text-text-primary sm:mt-0 sm:col-span-2 items-center"
              >
                <input
                  type="checkbox"
                  :value="notificationEmailsVal"
                  v-model="notificationEmailsVal"
                  v-on:change="saveEmailSettings()"
                  class="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                />
              </dd>
            </div>

            <div class="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4">
              <dt class="text-sm font-medium text-text-primary">
                {{ $t('profile.settings.mailing_list_emails') }}
                <p class="max-w-2xl text-xs text-text-secondary font-normal">
                  {{ $t('profile.settings.mailing_list_emails_text') }}
                </p>
              </dt>
              <dd
                class="mt-1 flex text-sm text-text-primary sm:mt-0 sm:col-span-2 items-center"
              >
                <input
                  type="checkbox"
                  :value="emailSignUpVal"
                  v-model="emailSignUpVal"
                  v-on:change="saveEmailSettings()"
                  class="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                />
              </dd>
            </div>
          </div>
        </SettingsCard>
      </section>

      <section
        v-show="activeTab === 'billing'"
        id="settings-panel-billing"
        role="tabpanel"
        aria-labelledby="settings-tab-billing"
        class="flex flex-col gap-6"
      >
        <SettingsCard
          :title="$t('profile.settings.billing_section')"
          :description="$t('profile.settings.billing_section_text')"
        >
          <div class="px-6">
            <dl class="divide-y divide-gray-800">
              <div
                class="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:pt-5"
                v-if="subscriptions.length == 0"
              >
                <dt class="text-text-primary">{{ $t('profile.settings.subscription') }}</dt>
                <dd class="mt-1 flex text-text-secondary sm:mt-0 sm:col-span-2">
                  <span class="flex-grow" v-if="user.access_level === 3">
                    {{ $t('profile.settings.team_tier_access') }}
                    <span v-if="user.has_canceled_sub"> {{ $t('profile.settings.cancelled_suffix') }} </span>
                    <span v-if="user.has_active_trial_code">
                      {{ $t('profile.settings.trial_ends', { date: moment(user.trial_expires_at).format("LLLL") }) }}
                    </span>
                    <span v-else-if="user.subscription_end_date">
                      {{ $t('profile.settings.ends_on', { date: moment(user.subscription_end_date).format("LL") }) }}
                    </span>
                  </span>
                  <span class="flex-grow" v-else-if="user.access_level === 2">
                    {{ $t('profile.settings.competitor_tier_access') }}
                    <span v-if="user.has_canceled_sub"> {{ $t('profile.settings.cancelled_suffix') }} </span>
                    <span v-if="user.has_active_trial_code">
                      {{ $t('profile.settings.trial_ends', { date: moment(user.trial_expires_at).format("LLLL") }) }}
                    </span>
                    <span v-else-if="user.subscription_end_date">
                      {{ $t('profile.settings.ends_on', { date: moment(user.subscription_end_date).format("LL") }) }}
                    </span>
                  </span>
                  <span class="flex-grow" v-else-if="user.access_level === 1">
                    {{ $t('profile.settings.player_tier_access') }}
                    <span v-if="user.has_canceled_sub"> {{ $t('profile.settings.cancelled_suffix') }} </span>
                    <span v-if="user.has_active_trial_code">
                      {{ $t('profile.settings.trial_ends', { date: moment(user.trial_expires_at).format("LLLL") }) }}
                    </span>
                    <span v-else-if="user.subscription_end_date">
                      {{ $t('profile.settings.ends_on', { date: moment(user.subscription_end_date).format("LL") }) }}
                    </span>
                  </span>
                  <span class="flex-grow" v-else>{{ $t('profile.settings.free_access') }}</span>
                  <span
                    class="ml-4 flex-shrink-0 flex items-start space-x-4"
                    v-if="
                      user.base_access
                        && user.has_active_subscription
                        && !user.has_canceled_sub
                    "
                  >
                    <a
                      :href="user.update_subscription_url || '#'"
                      class="bg-primary-600 px-5 py-2 rounded-md font-medium text-text-primary hover:text-text-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      target="_blank"
                      v-if="user.uses_paddle"
                    >
                      {{ $t('profile.settings.update') }}
                    </a>
                    <a
                      :href="user.cancel_subscription_url || '#'"
                      class="bg-primary-600 px-5 py-2 rounded-md font-medium text-text-primary hover:text-text-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      target="_blank"
                      v-if="user.uses_paddle"
                    >
                      {{ $t('common.cancel') }}
                    </a>
                  </span>
                </dd>
              </div>
              <div class="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4">
                <dt class="font-medium text-text-primary sm:col-span-1 my-auto">
                  {{ $t('profile.settings.redeem_code') }}
                </dt>
                <dd
                  class="mt-1 flex flex-col sm:flex-row items-stretch sm:items-center text-sm text-text-primary sm:mt-0 sm:col-span-2 gap-3 sm:gap-0"
                >
                  <input
                    @input="codeInput = $event.target.value"
                    class="block w-full sm:flex-1 rounded-md border-gray-700 bg-gray-800 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm text-text-secondary"
                    type="text"
                    :placeholder="$t('profile.settings.code_placeholder')"
                    :value="codeInput"
                  />
                  <Transition>
                    <button
                      type="button"
                      @click="redeemCode"
                      class="inline-flex w-full sm:w-auto items-center justify-center rounded-md border border-transparent bg-primary-600 px-4 py-2 font-medium text-text-primary shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:ml-3 sm:text-sm"
                    >
                      {{ $t('profile.settings.redeem') }}
                    </button>
                  </Transition>
                  <!-- <span class="ml-4 flex-shrink-0">
                    <button
                      type="button"
                      class="bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Update
                    </button>
                  </span> -->
                </dd>
              </div>
            </dl>
          </div>
        </SettingsCard>
        <template v-if="subscriptions.length > 0">
          <SettingsCard
            :title="$t('profile.settings.subscriptions_section')"
            :description="$t('profile.settings.subscriptions_section_text')"
          >
            <div class="py-4 flex flex-col">
              <div ref="codes" class="w-full overflow-x-auto px-6">
                <table class="min-w-max w-full">
                  <thead class="border-b border-gray-800">
                    <tr>
                      <th
                        scope="col"
                        class="py-3.5 pl-6 text-left pr-3 text-xs font-light text-text-secondary uppercase tracking-[.5px]"
                      >
                        {{ $t('profile.settings.subscription_type') }}
                      </th>
                      <th
                        scope="col"
                        class="px-3 py-3.5 text-left text-xs font-light text-text-secondary uppercase tracking-[.5px]"
                      >
                        {{ $t('profile.settings.status') }}
                      </th>
                      <th
                        scope="col"
                        class="px-3 py-3.5 text-left text-xs font-light text-text-secondary uppercase tracking-[.5px]"
                      >
                        {{ $t('profile.settings.period') }}
                      </th>
                      <th
                        scope="col"
                        class="px-3 py-3.5 text-left text-xs font-light text-text-secondary uppercase tracking-[.5px]"
                      >
                        {{ $t('profile.settings.cost') }}
                      </th>
                      <th
                        scope="col"
                        class="px-3 py-3.5 text-left text-xs font-light text-text-secondary uppercase tracking-[.5px]"
                      >
                        ID
                      </th>
                      <th
                        scope="col"
                        class="px-3 py-3.5 text-left text-xs font-light text-text-secondary uppercase tracking-[.5px]"
                      >
                        {{ $t('profile.settings.actions') }}
                      </th>
                    </tr>
                  </thead>
                  <tbody class="">
                    <tr
                      v-for="subscription in subscriptions"
                      :key="subscription"
                      class="text-center odd:bg-gray-900"
                    >
                      <td
                        class="whitespace-nowrap py-3 pl-6 pr-3 text-sm text-text-primary text-left flex gap-2 items-center"
                      >
                        <h1>
                          {{ subscriptionName(subscription) }}
                        </h1>
                      </td>
                      <td
                        class="whitespace-nowrap px-3 py-3 text-sm text-left"
                        :class="subscriptionStatusClass(subscription)"
                      >
                        {{ subscriptionStatusText(subscription) }}
                      </td>
                      <td
                        class="whitespace-nowrap px-3 py-3 text-sm text-text-primary text-left"
                      >
                        {{ subscriptionPeriod(subscription) }}
                      </td>
                      <td
                        class="whitespace-nowrap px-3 py-3 text-sm text-text-primary text-left"
                      >
                        {{ getCurrencySymbol(subscription?.currency) }}
                        {{ subscription.cost }}
                      </td>
                      <td
                        class="whitespace-nowrap px-3 py-3 text-sm text-text-primary text-left italic"
                      >
                        {{ subscription.id }}
                      </td>
                      <td
                        class="whitespace-nowrap px-3 py-3 text-sm text-text-primary text-left"
                      >
                        <button
                          class="text-red-400 underline underline-offset-4"
                          target="_blank"
                          v-if="subscription.status == 'active'"
                          @click="toggleUnsubscribeModal(subscription?.id)"
                        >
                          {{ $t('common.cancel') }}
                        </button>
                        <button
                          v-else-if="subscription.status == 'non_renewing'"
                          class="text-primary-400 underline underline-offset-4"
                          @click="reactivate_xsolla_subscription(subscription?.id)"
                        >
                          {{ $t('profile.settings.reactivate') }}
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </SettingsCard>
        </template>
        <SettingsCard
          :title="$t('profile.settings.my_codes_section')"
          :description="$t('profile.settings.my_codes_section_text')"
          id="my-codes"
        >
          <div class="py-4 flex flex-col">
            <div ref="codes" class="w-full overflow-x-auto px-6">
              <table class="min-w-max w-full">
                <thead class="border-b border-gray-800">
                  <tr>
                    <th
                      scope="col"
                      class="py-3.5 pl-6 text-left pr-3 text-xs font-light text-text-secondary uppercase tracking-[.5px]"
                    >
                      {{ $t('profile.settings.code') }}
                    </th>
                    <th
                      scope="col"
                      class="px-3 py-3.5 text-left text-xs font-light text-text-secondary uppercase tracking-[.5px]"
                    >
                      {{ $t('common.length') }}
                    </th>
                    <th
                      scope="col"
                      class="px-3 py-3.5 text-left text-xs font-light text-text-secondary uppercase tracking-[.5px]"
                    >
                      {{ $t('profile.settings.tier') }}
                    </th>
                    <th
                      scope="col"
                      class="px-3 py-3.5 text-left text-xs font-light text-text-secondary uppercase tracking-[.5px]"
                    >
                      {{ $t('profile.settings.valid_until') }}
                    </th>
                    <th
                      scope="col"
                      class="px-3 py-3.5 text-left text-xs font-light text-text-secondary uppercase tracking-[.5px]"
                    >
                      {{ $t('profile.settings.type') }}
                    </th>
                  </tr>
                </thead>
                <tbody class="">
                  <tr
                    v-for="code in trialCodes"
                    :key="code"
                    class="text-center odd:bg-gray-900"
                  >
                    <td
                      class="whitespace-nowrap py-3 pl-6 pr-3 text-sm text-text-primary text-left flex gap-2 items-center"
                    >
                      <h1>{{ code.code }}</h1>
                      <div
                        v-clipboard:copy="`${code.code}`"
                        v-clipboard:success="copySucceeded"
                      >
                        <img
                          src="../assets/icons/copy.svg"
                          :alt="$t('profile.settings.copy_icon_alt')"
                          class="w-4 h-4 cursor-pointer brightness-75 hover:scale-105 hover:brightness-100 duration-300"
                        />
                      </div>
                    </td>
                    <td
                      class="whitespace-nowrap px-3 py-3 text-sm text-text-primary text-left"
                    >
                      {{ $t('profile.settings.days', { n: code.duration_in_days }) }}
                    </td>
                    <td
                      class="whitespace-nowrap px-3 py-3 text-sm text-text-primary text-left"
                    >
                      {{ code.tier }}
                    </td>
                    <td
                      class="whitespace-nowrap px-3 py-3 text-sm text-text-primary text-left"
                    >
                      {{ code.valid_until ? moment(code.valid_until).format('LL') : $t('profile.settings.no_expiration') }}
                    </td>
                    <td
                      class="whitespace-nowrap px-3 py-3 text-sm text-text-primary text-left"
                    >
                      {{ getCodeType(code) }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </SettingsCard>
      </section>
    </main>
  </DashboardLayout>
  <UnsubscribeModal
    v-bind:showUnsubscribeModal="showUnsubscribeModal"
    :subscription_id="modalSubscription"
  />
  <ModalWindow :open="!!unlinkTarget">
    <div class="flex flex-col gap-6">
      <h1 class="text-text-primary text-[24px] font-semibold">
        {{ $t('profile.settings.unlink_confirm_title', { service: unlinkServices[unlinkTarget] }) }}
      </h1>
      <p class="text-text-secondary text-sm">
        {{ unlinkTarget === 'twitch' ? $t('profile.settings.unlink_twitch_confirm') : $t('profile.settings.unlink_confirm_body', { service: unlinkServices[unlinkTarget] }) }}
      </p>
      <div class="flex gap-4">
        <button
          @click="confirmUnlink"
          class="bg-danger-400 text-danger-950 px-4 py-2 rounded-md font-semibold hover:bg-danger-300 w-full duration-300"
        >
          {{ $t('profile.settings.unlink_confirm_yes') }}
        </button>
        <button
          @click="unlinkTarget = null"
          class="bg-gray-800 text-text-primary px-4 py-2 rounded-md font-medium hover:bg-gray-700 w-full duration-300"
        >
          {{ $t('common.cancel') }}
        </button>
      </div>
    </div>
  </ModalWindow>
  <ModalWindow :open="showSteamClearModal">
    <div class="flex flex-col gap-6">
      <h1 class="text-text-primary text-[24px] font-semibold flex gap-3 items-center">
        <img src="../assets/icons/error.svg" :alt="$t('profile.settings.steam_icon_alt')" class="w-8 h-8" />
        {{ $t('profile.settings.change_steam_account') }}
      </h1>
      <p class="text-text-secondary text-sm">
        {{ $t('profile.settings.change_steam_confirm') }}
      </p>
      <div v-if="steamCodeRequired">
        <label for="steam-change-code" class="block text-sm font-medium text-gray-100">
          {{ $t('profile.settings.email_code_label') }}
        </label>
        <input
          v-model="steamChangeCode"
          id="steam-change-code"
          type="text"
          inputmode="numeric"
          autocomplete="one-time-code"
          maxlength="6"
          placeholder="123456"
          class="mt-2 text-text-primary placeholder:text-text-secondary bg-gray-800 block w-full px-3 py-2 border border-gray-700 rounded-md sm:text-sm"
        />
      </div>
      <div class="flex gap-4">
        <button
          @click="unlinkSteamAccount"
          class="bg-danger-400 text-danger-950 px-4 py-2 rounded-md font-semibold hover:bg-danger-300 w-full duration-300"
        >
          {{ $t('profile.settings.change_steam_yes') }}
        </button>
        <button
          @click="showSteamClearModal = false; steamCodeRequired = false; steamChangeCode = ''"
          class="bg-gray-800 text-text-primary px-4 py-2 rounded-md font-medium hover:bg-gray-700 w-full duration-300"
        >
          {{ $t('common.cancel') }}
        </button>
      </div>
    </div>
  </ModalWindow>
  <ModalWindow :open="showChangeEmailModal">
    <h2 class="text-2xl text-text-primary font-semibold flex gap-2">
      <div class="w-8 h-8 rounded-full bg-gray-800 flex items-center justify-center">
        <img class="w-4" src="../assets/icons/error.svg" :alt="$t('profile.settings.refrag_icon_alt')" />
      </div>
      {{ $t('profile.settings.change_email_modal_title') }}
    </h2>
    <h3 class="text-text-secondary mt-3">
      {{ $t('profile.settings.change_email_modal_text') }}
    </h3>
    <div class="flex flex-row justify-end mt-6 w-full">
      <button
        class="bg-danger-500 hover:bg-danger-400 duration-300 px-4 py-2 rounded w-full"
        @click="updateEmail"
      >
        {{ $t('profile.settings.change_email_yes') }}
      </button>
      <button
        class="bg-gray-800 hover:bg-gray-700 duration-300 px-4 py-2 ml-2 rounded w-full text-text-primary"
        @click="showChangeEmailModal = false"
      >
        {{ $t('profile.settings.change_email_no') }}
      </button>
    </div>
  </ModalWindow>
  <ModalWindow :open="showDeleteAccountConfirmation">
    <h2 class="text-2xl text-text-primary font-semibold flex gap-2">
      <div class="w-8 h-8 rounded-full bg-gray-800 flex items-center justify-center">
        <img class="w-4" src="../assets/icons/error.svg" :alt="$t('profile.settings.refrag_icon_alt')" />
      </div>
      {{ $t('profile.settings.delete_account') }}
    </h2>
    <h3 class="text-text-secondary mt-3">
      {{ $t('profile.settings.delete_account_text') }}
    </h3>
    <div class="flex flex-col gap-4 mt-6">
      <div class="flex flex-col gap-2">
        <div v-if="wrongDeleteAccountCode" class="text-red-500 text-sm">
          {{ $t('profile.settings.invalid_verification_code') }}
        </div>
        <input
          type="text"
          v-model="deleteAccountCode"
          :placeholder="$t('profile.settings.verification_code_placeholder')"
          class="w-full px-4 py-2 rounded-md bg-gray-800 border border-gray-700 text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
        <button
          @click="resendDeleteVerification"
          class="text-sm text-primary-400 hover:text-primary-300 text-left"
        >
          {{ $t('profile.settings.resend_verification_code') }}
        </button>
      </div>
      <div class="flex flex-row justify-end w-full gap-2">
        <button
          class="bg-danger-500 hover:bg-danger-400 duration-300 px-4 py-2 rounded w-full disabled:opacity-50"
          :disabled="deleteAccountCode.length !== 6"
          @click="deleteAccount"
        >
          {{ $t('profile.settings.delete_account') }}
        </button>
        <button
          class="bg-gray-800 hover:bg-gray-700 duration-300 px-4 py-2 rounded w-full text-text-primary"
          @click="showDeleteAccountConfirmation = false"
        >
          {{ $t('common.cancel') }}
        </button>
      </div>
    </div>
  </ModalWindow>
</template>

<script>
import { ref } from 'vue';
import axios from 'axios';
import { XMarkIcon, CheckIcon } from '@heroicons/vue/20/solid';
import { mapMutations } from 'vuex';
import {
  latencyLabel, latencyLabelFor, loadServerLocations, serverLocations, sortByLatency,
} from '../utils/server_locations.js';
import { loadUserPreferences, resolveServerLocation, setServerLocation } from '../utils/user_preferences.js';
import { clearSignedOutUser } from '../utils/session';
import icon from '../assets/logos/refrag-icon-button.svg';
import UnsubscribeModal from '../components/UnsubscribeModal.vue';
import ModalWindow from '../components/ModalWindow.vue';
import CoachSettings from '../components/CoachSettings.vue';
import AudioSettings from '../components/Settings/AudioSettings.vue';
import TwitchIcon from '../components/LanParty/TwitchIcon.vue';
import { startTwitchLink } from '../utils/lan_party';
import SettingsCard from '../components/Settings/SettingsCard.vue';
import SettingsDropdown from '../components/Settings/SettingsDropdown.vue';
import DashboardLayout from '../components/layouts/DashboardLayout.vue';
import { railsErrorToMessage } from '../utils/error_utils';
import { visibleTabs, tabFromQuery } from '../utils/settings_tabs';

export default {
  head() {
    return { title: this.$t('profile.settings.page_title') };
  },
  components: {
    UnsubscribeModal,
    XMarkIcon,
    TwitchIcon,
  },
  setup() {
    const automaticTimezoneEnabled = ref(true);
    const autoUpdateApplicantDataEnabled = ref(false);

    return {
      automaticTimezoneEnabled,
      autoUpdateApplicantDataEnabled,
    };
  },
  data() {
    return {
      activeTab: tabFromQuery(this.$route.query.tab, this.$auth.user()),
      syncingDiscord: false,
      unlinkTarget: null,
      unlinkServices: { discord: 'Discord', faceit: 'Faceit', twitch: 'Twitch' },
      userIsSure: false,
      codeInput: '',
      betaAccessVal: false,
      streamerModVal: false,
      showUnsubscribeModal: false,
      trialCodes: [],
      accessCodeEmailsVal: null,
      notificationEmailsVal: null,
      steam_bot_match_notifications: null,
      steam_bot_routine_notifications: null,
      steam_bot_server_notifications: null,
      emailSignUpVal: null,
      steamMatchCode: '',
      authenticationCode: '',
      subscriptions: [],
      modalSubscription: null,
      showSteamClearModal: false,
      // Set once the backend has emailed a code that must confirm the Steam change.
      steamCodeRequired: false,
      steamChangeCode: '',
      showChangeEmailModal: false,
      userSettings: {},
      steamBotLink: null,
      websocket: null,
      subscription: null,
      // allstarEnabledVal: false, // Allstar highlights disabled
      localSelectedLocation: null,
      languages: [
        { name: 'English', code: 'en' },
        { name: 'Português', code: 'pt_t' },
        { name: 'Danish', code: 'dk' },
        { name: 'Chinese', code: 'cn' },
        { name: 'Deutsch', code: 'de' },
        { name: 'Polish', code: 'pl' },
      ],
      localSelectedLanguage: null,
      showDeleteAccountConfirmation: false,
      deleteAccountCode: '',
      wrongDeleteAccountCode: false,
    };
  },
  computed: {
    tabs() {
      return visibleTabs(this.$auth.user());
    },
    canUseMatchmaking() {
      return !!this.$auth.user()?.can_use_matchmaking;
    },
    hidDetectionAvailable() {
      return !!navigator?.hid;
    },
    user() {
      return { ...this.$auth.user(), ...this.userSettings };
    },
    emailSettings() {
      return this.user.email_settings;
    },
    locations() {
      return sortByLatency(serverLocations.value);
    },
    activeLocation() {
      return this.localSelectedLocation || resolveServerLocation(this.locations);
    },
    locationOptions() {
      return this.locations.map((location) => ({ value: location.id, label: location.name, location }));
    },
    languageOptions() {
      return this.languages.map((language) => ({
        value: language.code,
        label: this.$t(`profile.settings.languages.${language.code}`),
      }));
    },
    username() {
      return this.user.username || this.user.steam_profile?.personaname
        || this.user.twitch?.display_name || this.user.steam_id;
    },
    discord_tag() {
      return this.user.discord_tag;
    },
    imageURL() {
      return this.user.steam_profile?.avatarfull || this.user.twitch?.avatar_url || icon;
    },
    selectedLanguage() {
      if (this.localSelectedLanguage) {
        return this.localSelectedLanguage;
      } if (!this.user.language) {
        return this.languages[0];
      }
      return this.languages.find((language) => language.code === this.user.language);
    },
  },
  methods: {
    selectTab(tab) {
      this.activeTab = tab;
      this.$router.replace({ query: { ...this.$route.query, tab }, hash: '' });
    },
    // A hash link (/settings#beta-access) opens the tab holding its target first.
    scrollToHash() {
      if (!this.$route.hash) return;
      // By id, not as a selector: a hash such as #123 is not a valid one and would throw
      let id = this.$route.hash.slice(1);
      try {
        id = decodeURIComponent(id);
      } catch {
        // A malformed escape, such as #100%: look it up as written
      }
      const el = document.getElementById(id);
      if (!el) return;
      const panel = el.closest('[role="tabpanel"]');
      if (panel) this.activeTab = panel.id.replace('settings-panel-', '');
      this.$nextTick(() => el.scrollIntoView({ block: 'start', behavior: 'smooth' }));
    },
    ...mapMutations(['showMessage', 'hideMessage']),
    latencyLabel,
    latencyLabelFor,
    handleCoachSettingsUpdate() {
      // Refresh user data after coach settings are updated
      this.$auth.fetch();
    },
    selectLocation(location) {
      setServerLocation(location);
      this.localSelectedLocation = location;
    },
    getSteamBot() {
      axios({
        method: 'get',
        url: '/steam_bots/bot',
      }).then((response) => {
        this.steamBotLink = response.data.data.invite_link;
      });
    },
    getLocations() {
      return Promise.all([loadServerLocations(), loadUserPreferences()]);
    },
    deleteAccount() {
      axios({
        method: 'delete',
        url: '/users/delete_me',
        params: {
          token: this.deleteAccountCode,
        },
      }).then(() => {
        this.showDeleteAccountConfirmation = false;
        clearSignedOutUser();
        this.$auth.logout();
      }).catch((err) => {
        this.wrongDeleteAccountCode = true;
        setTimeout(() => {
          this.wrongDeleteAccountCode = false;
        }, 5000);
      });
    },
    toggleDeleteAccountConfirmation() {
      axios({
        method: 'post',
        url: '/users/request_delete_me_token',
      }).then(() => {
        this.showDeleteAccountConfirmation = !this.showDeleteAccountConfirmation;
      });
    },
    subscribeToSteamBot() {
      if (this.subscription) return;

      this.websocket = this.$cable.connect();
      this.subscription = this.websocket.subscriptions.create(
        { channel: 'SteamBotChannel' },
        {
          received: (data) => {
            if (data.event === 'steam_bot_added') {
              this.$auth.fetch();
            }
          },
        },
      );
    },
    selectLocationById(id) {
      this.selectLocation(this.locations.find((location) => location.id === id));
    },
    selectLanguageByCode(code) {
      this.selectLanguage(this.languages.find((language) => language.code === code));
    },
    selectLanguage(language) {
      this.localSelectedLanguage = language;
      axios({
        method: 'put',
        url: '/users/update_user',
        data: {
          language: language.code,
        },
      }).then(() => {
        this.$auth.fetch();
      });
    },
    getRecurringText(subscription) {
      switch (subscription.duration_in_months) {
        case 1:
          return this.$t('profile.settings.monthly');
        case 3:
          return this.$t('profile.settings.quarterly');
        case 6:
          return this.$t('profile.settings.bi_annually');
        case 12:
          return this.$t('profile.settings.annually');
        default:
          return this.$t('profile.settings.unknown');
      }
    },
    getCurrencySymbol(currency) {
      if (!currency) return;
      return (0)
        .toLocaleString('en-US', {
          style: 'currency',
          currency,
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })
        .replace(/\d/g, '')
        .trim();
    },
    toggleEmailChangeModal() {
      this.showChangeEmailModal = !this.showChangeEmailModal;
    },
    showSteamClearConfirmation() {
      this.showSteamClearModal = true;
    },
    showChangeSteamAccount() {
      return (
        !this.user.steam_id_last_changed_at
        || moment().diff(this.user.steam_id_last_changed_at, 'days') >= 30
      );
    },
    updateEmail() {
      axios({
        method: 'post',
        url: '/users/request_email_change',
      }).then((res) => {
        this.showChangeEmailModal = false;
        this.showMessage({
          messageType: 'success',
          messageText: this.$t('profile.settings.email_sent_toast'),
        });
        setTimeout(() => {
          this.hideMessage();
        }, 3000);
      });
    },
    unlinkSteamAccount() {
      axios({
        method: 'put',
        url: '/users/clear_steam_id',
        data: { code: this.steamChangeCode || undefined },
      }).then((res) => {
        this.$auth.fetch();
        this.$router.push({ name: 'Dashboard' });
      }).catch((err) => {
        if (err.response?.data?.steam_change_code_required) this.steamCodeRequired = true;
        const messageText = err.response?.data?.errors?.[0] || this.$t('common.something_went_wrong');
        this.showMessage({ messageType: 'error', messageText });
      });
    },
    subscriptionName(subscription) {
      const name = subscription.subscription_tier.name.charAt(0).toUpperCase()
        + subscription.subscription_tier.name.slice(1);
      return `${name} (${this.getRecurringText(subscription)})`;
    },
    subscriptionStatusText(subscription) {
      if (subscription.status === 'active') {
        return this.$t('profile.settings.status_active');
      }
      if (subscription.status == 'non_renewing') {
        return this.$t('profile.settings.status_non_renewing');
      }
      return this.$t('profile.settings.status_cancelled');
    },
    subscriptionStatusClass(subscription) {
      if (subscription.status === 'active') {
        return 'text-green-500';
      }
      if (subscription.status == 'non_renewing') {
        return 'text-yellow-500';
      }
      return 'text-red-500';
    },
    subscriptionPeriod(subscription) {
      return `${moment(subscription.date_create).format('LL')} - ${moment(
        subscription.date_next_charge,
      ).format('LL')}`;
    },
    getCodeType(code) {
      if (code.ignore_limit) return this.$t('profile.settings.type_access');
      return this.$t('profile.settings.type_trial');
    },
    copySucceeded() {
      this.showMessage({
        messageType: 'success',
        messageText: this.$t('profile.settings.code_copied'),
      });
      setTimeout(() => {
        this.hideMessage();
      }, 3000);
    },
    async getKeyboard() {
      const device = await navigator?.hid?.requestDevice({
        filters: [
          {
            vendorId: 0x31e3,
          },
        ],
      });
      if (device[0]) {
        this.updateUser({ uses_wooting: true }).then(() => {
          this.$auth.fetch();
        });
      }
    },
    async getTrialCodes() {
      axios({
        method: 'get',
        url: 'trial_codes',
        data: {},
      }).then((data) => {
        this.trialCodes = data.data;
      });
    },
    async getSubscriptions() {
      axios({
        method: 'get',
        url: 'subscriptions/my_subscriptions',
        data: {},
      }).then((data) => {
        this.subscriptions = data.data;
      });
    },
    link_faceit() {
      axios({
        method: 'get',
        url: '/faceit/start_authentication',
        data: {},
      }).then((data) => {
        window.location = data.data.url;
      });
    },
    confirmUnlink() {
      const unlink = { discord: this.unlink_discord, faceit: this.unlink_faceit, twitch: this.unlink_twitch };
      unlink[this.unlinkTarget]();
      this.unlinkTarget = null;
    },
    unlink_faceit() {
      axios({
        method: 'post',
        url: '/faceit/unlink',
        data: {},
      }).then(() => {
        window.location.reload();
      });
    },
    link_discord() {
      axios({
        method: 'get',
        url: '/discord/link_discord',
        data: {},
      }).then((data) => {
        window.location = data.data.url;
      });
    },
    link_twitch() {
      startTwitchLink().catch(() => {
        this.showMessage({ messageType: 'error', messageText: this.$t('profile.settings.link_twitch_failed') });
      });
    },
    unlink_twitch() {
      axios.delete('/twitch/link').then(() => {
        window.location.reload();
      }).catch(() => {
        this.showMessage({ messageType: 'error', messageText: this.$t('profile.settings.unlink_twitch_failed') });
      });
    },
    unlink_discord() {
      axios({
        method: 'post',
        url: '/discord/unlink_discord',
        data: {},
      }).then(() => {
        window.location.reload();
      });
    },
    cancel_xsolla_subscription() {
      const confirmed = confirm(this.$t('profile.settings.cancel_subscription_confirm'));
      if (!confirmed) return;

      this.$gtm.trackEvent({
        event: 'cancel_subscription',
      });
    },
    reactivate_xsolla_subscription(subscription_id) {
      this.$gtm.trackEvent({
        event: 'reactivate_subscription',
      });

      axios({
        method: 'post',
        url: `/subscriptions/${subscription_id}/reactivate`,
        data: {},
      }).then(() => {
        window.location.reload();
      });
    },
    toggleUnsubscribeModal(subscription_id) {
      this.modalSubscription = subscription_id;
      this.showUnsubscribeModal = !this.showUnsubscribeModal;
    },
    redeemCode() {
      axios({
        url: '/trial_codes/redeem',
        method: 'POST',
        headers: {},
        data: {
          code: this.codeInput,
        },
      })
        .then((res) => {
          this.$auth.fetch();
          this.showMessage({
            messageType: 'success',
            messageText: this.$t('profile.settings.code_redeemed_toast'),
          });
          window.location.reload();
        })
        .catch((err) => {
          this.showMessage({
            messageType: 'error',
            messageText: err.response.data.errors[0],
          });
        });
    },
    async updateUser(data) {
      return axios({
        method: 'put',
        url: '/users/update_user',
        data,
      }).then(() => {
        this.$auth.fetch();
      });
    },
    saveSteamMatchCode() {
      if (
        !/^CSGO-[A-Za-z0-9]{5}-[A-Za-z0-9]{5}-[A-Za-z0-9]{5}-[A-Za-z0-9]{5}-[A-Za-z0-9]{5}$/.test(
          this.steamMatchCode,
        )
      ) {
        this.showMessage({
          messageType: 'error',
          messageText: this.$t('profile.settings.invalid_match_code'),
        });
        return;
      }
      axios({
        method: 'post',
        url: '/matches',
        data: {
          match: {
            match_platform: 'valve',
            match_token: this.steamMatchCode,
          },
        },
      })
        .then(() => {
          this.$auth.fetch();
          this.showMessage({
            messageType: 'success',
            messageText: this.$t('profile.settings.match_code_saved_toast'),
          });
        })
        .catch((err) => {
          this.showMessage({
            messageType: 'error',
            messageText: this.$t('profile.settings.match_token_error', { error: err.response.data.match_token[0] }),
          });
        });
    },
    saveSteamAuthCode() {
      if (!/^[A-Z0-9]{4}-[A-Z0-9]{5}-[A-Z0-9]{4}$/.test(this.authenticationCode)) {
        this.showMessage({
          messageType: 'error',
          messageText: this.$t('profile.settings.invalid_auth_code'),
        });
        return;
      }
      axios({
        method: 'put',
        url: '/users/update_user',
        data: {
          match_auth_token: this.authenticationCode,
        },
      }).then((res) => {
        this.$auth.fetch();
        this.authenticationCode = res.data.data.match_auth_token;
        this.showMessage({
          messageType: 'success',
          messageText: this.$t('profile.settings.auth_code_saved_toast'),
        });
      }).catch((err) => {
        this.showMessage({
          messageType: 'error',
          messageText: railsErrorToMessage(err),
        });
      });
    },
    clearMatchCode() {
      this.steamMatchCode = '';
    },
    clearSteamAuthCode() {
      axios({
        method: 'put',
        url: '/users/update_user',
        data: {
          match_auth_token: '',
        },
      }).then((res) => {
        this.$auth.fetch();
        this.authenticationCode = '';
        this.showMessage({
          messageType: 'success',
          messageText: this.$t('profile.settings.auth_code_cleared_toast'),
        });
      });
    },
    getUserEmailSettings() {
      this.accessCodeEmailsVal = this.user.email_settings.receive_email_for_access_codes || false;
      this.notificationEmailsVal = this.user.email_settings.receive_email_for_notifications || false;
      this.emailSignUpVal = this.user.email_settings.email_sign_up || false;
    },
    getSteamBotSettings() {
      this.steam_bot_match_notifications = this.user.steam_bot_match_notifications || false;
      this.steam_bot_routine_notifications = this.user.steam_bot_routine_notifications || false;
      this.steam_bot_server_notifications = this.user.steam_bot_server_notifications || false;
    },
    syncDiscordRoles() {
      if (this.syncingDiscord) return;
      this.syncingDiscord = true;
      axios({
        method: 'post',
        url: '/users/sync_roles',
      })
        .then(() => {
          this.showMessage({
            messageType: 'success',
            messageText: this.$t('profile.settings.roles_synced_toast'),
          });
          setTimeout(() => {
            this.hideMessage();
          }, 3000);
        })
        .finally(() => {
          this.syncingDiscord = false;
        });
    },
    async getSettings() {
      return axios({
        method: 'get',
        url: '/users/my_settings',
      }).then((res) => {
        this.userSettings = res.data;
        this.betaAccessVal = !!this.user.is_beta_tester;
        this.streamerModVal = !!this.user.streamer_mode;
        // this.allstarEnabledVal = !!this.user.enable_allstar; // Allstar highlights disabled

        if (this.user.match_auth_token && this.user.match_auth_token.length > 0) {
          this.user.match_auth_token === '********'
            ? (this.authenticationCode = '')
            : (this.authenticationCode = this.user.match_auth_token);
        }

        if (this.user.has_valve_matches) {
          this.steamMatchCode = 'CSGO - ***** - ***** - ***** - *****';
        }
      });
    },
    saveEmailSettings() {
      axios({
        method: 'put',
        url: '/users/update_user',
        data: {
          receive_email_for_access_codes: this.accessCodeEmailsVal,
          receive_email_for_notifications: this.notificationEmailsVal,
          email_sign_up: this.emailSignUpVal,
        },
      }).then(() => {
        this.$auth.fetch();
        this.showMessage({
          messageType: 'success',
          messageText: this.$t('profile.settings.email_settings_saved_toast'),
        });
      });
    },
    saveSteamBotSettings() {
      axios({
        method: 'put',
        url: '/users/update_user',
        data: {
          steam_bot_match_notifications: this.steam_bot_match_notifications,
          steam_bot_routine_notifications: this.steam_bot_routine_notifications,
          steam_bot_server_notifications: this.steam_bot_server_notifications,
        },
      }).then(() => {
        this.$auth.fetch();
        this.showMessage({
          messageType: 'success',
          messageText: this.$t('profile.settings.bot_settings_saved_toast'),
        });
      });
    },
    // Allstar highlights disabled
    // saveAllstarEnabled() {
    //   axios({
    //     method: 'put',
    //     url: '/users/update_user',
    //     data: {
    //       enable_allstar: this.allstarEnabledVal,
    //     },
    //   })
    //     .then(() => {
    //       this.$auth.fetch();
    //       this.showMessage({
    //         messageType: 'success',
    //         messageText: 'Updated Allstar Settings Successfully',
    //       });
    //     })
    //     .catch((err) => {
    //       this.showMessage({
    //         messageType: 'error',
    //         messageText: err.response.data.message,
    //       });
    //     });
    // },
    saveBetaAccess() {
      axios({
        method: 'put',
        url: '/users/update_user',
        data: {
          is_beta_tester: this.betaAccessVal,
        },
      })
        .then(() => {
          this.$auth.fetch();
          this.showMessage({
            messageType: 'success',
            messageText: this.$t('profile.settings.beta_access_saved_toast'),
          });
        })
        .catch((err) => {
          this.showMessage({
            messageType: 'error',
            messageText: err.response.data.message,
          });
        });
    },
    saveStreamerMode() {
      axios({
        method: 'put',
        url: '/users/update_user',
        data: {
          streamer_mode: this.streamerModVal,
        },
      })
        .then(() => {
          this.$auth.fetch();
          this.showMessage({
            messageType: 'success',
            messageText: this.$t('profile.settings.streamer_mode_saved_toast'),
          });
        })
        .catch((err) => {
          this.showMessage({
            messageType: 'error',
            messageText: err.response.data.message,
          });
        });
    },
  },
  mounted() {
    this.getSettings().then(() => {
      this.getTrialCodes();
      this.getSubscriptions();
      this.getLocations();
      this.getUserEmailSettings();
      this.getSteamBotSettings();
      this.getSteamBot();
      this.subscribeToSteamBot();

      this.$nextTick(this.scrollToHash);
    });
  },
  watch: {
    '$route.hash': 'scrollToHash',
    // A hash link picks its tab from where its target is (scrollToHash), whatever ?tab= says
    '$route.query.tab': function routeTab(tab) {
      if (!this.$route.hash) this.activeTab = tabFromQuery(tab, this.$auth.user());
    },
    // Matchmaking access can land after mount (a fresh /me), or go: re-pick so
    // ?tab=audio opens once granted, and the Audio tab closes once revoked
    canUseMatchmaking() {
      if (!this.$route.hash) this.activeTab = tabFromQuery(this.$route.query.tab, this.$auth.user());
    },
  },
};
</script>

<style>
.v-enter-active,
.v-leave-active {
  transition: opacity 0.2s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}
</style>
